import React, {
  use,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";

import { useInView } from "react-intersection-observer";
import { loadImage } from "../services/DataService";
import Layout from "./Layout";
import HomeCenterBar from "../ui/compoP/CenterBars/HomeCenterBar.jsx";
import ItemCard from "../ui/compoP/ItemCard.jsx";
import { fetchImage, getItems } from "../services/itemsServices.js";
import SvgIcn from "../data/IconCompo.js";
import { useLocation, useNavigate } from "react-router-dom";
import HomeLeftBar from "../ui/compoP/LeftBars/HomeLeftBar.jsx";
import { LazyItemCard } from "../ui/compoP/LazyItemCard.jsx";
import { getMyFavorites } from "../services/pbFavorites.js";
import capitalizeFirstLetter from "../utils/Capitalize.js";
import {
  resolveFavoriteItems,
  findPocketBaseFavoriteId,
  favoriteIdMatches,
} from "../services/favoriteResolver.js";
import { getCardsLogoUrl, isCardsLogoRaster } from "../services/cardsLogoMap.js";

function HomePage({ selectedMode = "tools" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [totalItemsNumber, setTotalItemsNumber] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");

  const [items, setItems] = useState([]);
  /** Raw `itemId` strings from PocketBase (legacy mongo, img names, or csv ids). */
  const [favPbIds, setFavPbIds] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [favsItems, setFavsItems] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const contentRef = useRef(null);
  const [loadMoreScrollRoot, setLoadMoreScrollRoot] = useState(null);
  /** Prevents duplicate concurrent CSV page fetches (IO + layout chain). */
  const csvAppendBusyRef = useRef(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const ITEMS_PER_PAGE = 60;

  const setContentWrapperNode = useCallback((node) => {
    contentRef.current = node;
    setLoadMoreScrollRoot(node);
  }, []);

  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    threshold: 0,
    root: loadMoreScrollRoot ?? undefined,
    rootMargin: "0px 0px 480px 0px",
  });

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.name]: e.value,
    }));
  };
  const modeIcons = useMemo(
    () => ({
      tools: { icon: "tool", text: "Tools" },
      ideaz: { icon: "idea", text: "ideaz" },
      resos: { icon: "resos", text: "resos" },
    }),
    []
  );

  useEffect(() => {
    localStorage.setItem("mode", selectedMode);
    setTimeout(() => {
      localStorage.setItem("items", JSON.stringify(filteredItems));
    }, 500);
  }, [selectedMode, filteredItems]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleFormChange({ name: "search", value: debouncedSearch });
    }, 300);
    if (debouncedSearch) {
      setIsSearch(true);
    }

    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleClearFilters = () => {
    setFilteredItems(items);
    setFormData({});
    setDebouncedSearch("");
  };

  useEffect(() => {
    handleClearFilters();
    setCatalogTotal(0);
  }, [selectedMode]);

  const itemIsFavorite = useCallback(
    (item) => {
      if (!item) return false;
      if (favsItems.some((f) => f._id === item._id)) return true;
      return favPbIds.some((fid) => favoriteIdMatches(fid, item));
    },
    [favPbIds, favsItems]
  );

  const toogleLike = (item) => {
    const was = itemIsFavorite(item);
    setFavPbIds((prev) => {
      if (was) {
        return prev.filter((fid) => !favoriteIdMatches(fid, item));
      }
      return [...prev, item._id];
    });
    setFavsItems((prev) => {
      if (was) {
        return prev.filter((f) => f._id !== item._id);
      }
      if (prev.some((f) => f._id === item._id)) return prev;
      return [...prev, { ...item, __pbFavoriteKeys: [item._id] }];
    });
  };
  useEffect(() => {
    // Load favorites from PocketBase, then resolve items for local Fav filter
    (async () => {
      try {
        const [favsGrouped, result] = await Promise.all([
          getMyFavorites(),
          getItems(selectedMode, "ALL", "", [], [], 1, 1000),
        ]);
        const favIds = favsGrouped[`my${capitalizeFirstLetter(selectedMode)}`] || [];
        setFavPbIds(favIds);

        const list = result.items || [];
        const n = result.totalItemsNumber ?? list.length;
        setCatalogTotal(typeof n === "number" ? n : list.length);
        const favItems = await resolveFavoriteItems(favIds, list);
        setFavsItems(favItems);
      } catch (err) {
        console.log(err);
        setFavPbIds([]);
        setFavsItems([]);
        setCatalogTotal(0);
      }
    })();
  }, [selectedMode]);

  useEffect(() => {
    setPage(1);
    filterItems(
      selectedMode,
      formData.tag,
      formData.search,
      [formData.check],
      [formData.radio],
      1,
      ITEMS_PER_PAGE
    );
  }, [formData]);

  useEffect(() => {
    if (formData.tag !== "Favs") return;
    filterItems(
      selectedMode,
      formData.tag,
      formData.search,
      [formData.check],
      [formData.radio],
      1,
      ITEMS_PER_PAGE
    );
    // Re-run when favorites resolve to catalog rows (IDs migrated from legacy API to CSV)
  }, [favsItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch items when selectedMode or formData changes

  const filterItems = (
    mode,
    tag,
    search,
    platform,
    price,
    currentPage,
    limit
  ) => {
    // Handle favorites filtering locally
    if (tag === "Favs") {
      setLoading(true);
      if (currentPage === 1 && contentRef.current) {
        contentRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      let filtered = [...favsItems];

      // Apply search filter if search term exists
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.name?.toLowerCase().includes(searchLower) ||
            item.description?.[0]?.toLowerCase().includes(searchLower) ||
            item.tags?.some((t) => t.toLowerCase().includes(searchLower))
        );
      }

      // Apply platform filter if exists
      if (platform && platform.length > 0 && platform[0]) {
        const pf = platform[0];
        filtered = filtered.filter((item) =>
          item.platform?.some(
            (p) =>
              p === pf ||
              p?.toLowerCase?.() === pf?.toLowerCase?.()
          )
        );
      }

      // Apply price filter if exists
      if (price && price.length > 0 && price[0]) {
        filtered = filtered.filter((item) => item.price === price[0]);
      }

      // Handle pagination
      const startIndex = (currentPage - 1) * limit;
      const paginatedItems = filtered.slice(startIndex, startIndex + limit);

      setTotalItemsNumber(filtered.length);

      if (currentPage === 1) {
        setFilteredItems(paginatedItems);
      } else {
        setFilteredItems((prev) => [...prev, ...paginatedItems]);
      }

      setHasMore(paginatedItems.length === limit);
      setLoading(false);
      return;
    }

    // Original API call for non-favorites
    if (currentPage > 1) {
      if (csvAppendBusyRef.current) return;
      csvAppendBusyRef.current = true;
    }

    setLoading(true);
    if (currentPage === 1 && contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    getItems(mode, tag, search, platform, price, currentPage, limit)
      .then((result) => {
        const list = result?.items ?? [];
        const tn = result?.totalItemsNumber;
        const totalNum =
          typeof tn === "number" && !Number.isNaN(tn)
            ? tn
            : tn != null && tn !== ""
            ? parseInt(String(tn), 10)
            : NaN;
        const validTotal = !Number.isNaN(totalNum);
        if (currentPage === 1) {
          setTotalItemsNumber(
            tn !== undefined && tn !== null && tn !== "" ? tn : 0
          );
          setFilteredItems(list);
        } else {
          setFilteredItems((prev) => [...prev, ...list]);
        }
        const loadedSoFar = (currentPage - 1) * limit + list.length;
        setHasMore(
          validTotal ? loadedSoFar < totalNum : list.length === limit
        );
      })
      .catch(() => {
        if (currentPage === 1) {
          setFilteredItems([]);
          setTotalItemsNumber(0);
        }
        setHasMore(false);
      })
      .finally(() => {
        if (currentPage > 1) csvAppendBusyRef.current = false;
        setLoading(false);
      });
  };

  const filterItemsRef = useRef(filterItems);
  filterItemsRef.current = filterItems;

  /** When the user is scrolled to the bottom, new rows can move the IO sentinel out of view; fetch the next page anyway. */
  useLayoutEffect(() => {
    if (loading || !hasMore) return;
    const fd = formDataRef.current;
    if (fd.tag === "Favs") return;
    const n = filteredItems.length;
    if (n === 0 || n % ITEMS_PER_PAGE !== 0) return;
    const sc = contentRef.current;
    if (!sc) return;
    const gap = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (gap > 520) return;
    const nextPage = n / ITEMS_PER_PAGE + 1;
    filterItemsRef.current(
      selectedMode,
      fd.tag,
      fd.search,
      [fd.check],
      [fd.radio],
      nextPage,
      ITEMS_PER_PAGE
    );
    setPage(nextPage);
  }, [filteredItems.length, loading, hasMore, selectedMode]);

  useEffect(() => {
    if (loadMoreInView && !loading && hasMore) {
      setPage((prev) => {
        const next = prev + 1;
        filterItems(
          selectedMode,
          formData.tag,
          formData.search,
          [formData.check],
          [formData.radio],
          next,
          ITEMS_PER_PAGE
        );
        return next;
      });
    }
  }, [loadMoreInView, loading, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayItems = useMemo(
    () => (Array.isArray(filteredItems) ? filteredItems : []),
    [filteredItems]
  );

  const handleLanguageChange = (value) => {
    setSelectedLanguage(value.value);
    // Store the preference in localStorage

    localStorage.setItem("preferredLanguage", value.value);
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem("preferredLanguage");
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage);
    }
  }, []);

  const memorizedCard = useMemo(() => {
    return displayItems.map((item) => {
      const favRow = favsItems.find((f) => f._id === item._id);
      const merged = favRow
        ? { ...item, __pbFavoriteKeys: favRow.__pbFavoriteKeys }
        : item;
      return (
        <Card
          key={merged._id}
          item={merged}
          selectedMode={selectedMode}
          toogleLike={toogleLike}
          selectedLanguage={selectedLanguage}
          isFavorite={itemIsFavorite(merged)}
          favoriteToggleId={findPocketBaseFavoriteId(favPbIds, merged)}
        />
      );
    });
  }, [selectedMode, displayItems, selectedLanguage, itemIsFavorite, favsItems]);

  return (
    <Layout
      leftBar={<HomeLeftBar selectedMode={selectedMode} />}
      centerBar={
        <HomeCenterBar
          formData={formData}
          favsNumber={favPbIds?.length}
          debouncedSearch={debouncedSearch}
          handleFormChange={handleFormChange}
          handleClearFilters={handleClearFilters}
          selectedMode={selectedMode}
          itemsNumber={filteredItems?.length}
          totalItemsNumber={totalItemsNumber}
          allCatalogCount={catalogTotal}
          setDebouncedSearch={setDebouncedSearch}
          selectedLanguage={selectedLanguage}
          onLanguageChange={handleLanguageChange}
          icon={
            selectedMode === "tools"
              ? "tool"
              : selectedMode === "ideaz"
              ? "idea"
              : selectedMode
          }
        />
      }
      selectedMode={selectedMode}
      contentRef={setContentWrapperNode}
    >
      <div className="cards-grid">{memorizedCard}</div>
      {/* Load more trigger element */}
      <div ref={loadMoreRef} className="end-of-content-box">
        {false && (
          <div className="loading-spinner-box">
            <SvgIcn
              name={modeIcons[selectedMode].icon}
              className="shadow-icon"
            />
            <p>{modeIcons[selectedMode].text}...</p>
          </div>
        )}
        {!loading && !hasMore && filteredItems?.length > 0 && (
          <div className="end-of-content">
            <SvgIcn name="check" className="shadow-icon" />
            <p>
              You've seen it all! <br /> No more items to load.
            </p>
          </div>
        )}
        {!loading && filteredItems?.length === 0 && (
          <div className="end-of-content">
            <SvgIcn name="no_item" className="shadow-icon" />
            {/* <p>
              No Items
              <br /> Found!
            </p> */}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HomePage;

const Card = ({
  item,
  selectedMode,
  toogleLike,
  selectedLanguage,
  isFavorite,
  favoriteToggleId,
}) => {
  const [loadedImages, setLoadedImages] = useState({});

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true, // Load each image only once
  });

  useEffect(() => {
    if (!inView || item._id == null) return;
    if (item._id in loadedImages) return;
    loadImage(item).then((img) => {
      setLoadedImages((prev) => ({
        ...prev,
        [item._id]: img,
      }));
    });
  }, [inView, item, loadImage, loadedImages]);

  return (
    <div ref={ref}>
      <ItemCard
        key={item._id}
        data={{
          ...item,
          description:
            selectedLanguage !== "English"
              ? [item.description?.[1] || "", item.description?.[0] || ""]
              : [item.description?.[0] || "", item.description?.[1] || ""],
          category: selectedMode,
          platform: item.platform || [],
          price: item.price || "free",
          links: {
            android: item.linkAndroid || "",
            apple: item.linkApple || item.link || "",
            windows: item.linkWindows || "",
          },
          image: loadedImages[item._id] || item.image,
        }}
        imageUrl={
          item._source === "csv" && item.img
            ? getCardsLogoUrl(item.img) || undefined
            : undefined
        }
        bundledLogoIsWebp={
          item._source === "csv" && item.img ? isCardsLogoRaster(item.img) : false
        }
        selectedMode={selectedMode}
        isFavorite={isFavorite}
        favoriteToggleId={favoriteToggleId}
        toogleLike={toogleLike}
        page="home"
      />
    </div>
  );
};
